/**
 * OrderTimelineTab — Chronological timeline of order lifecycle events.
 *
 * Fetches aggregated timeline data from the backend and renders
 * a vertical timeline showing all events (email received, order
 * created, state transitions, BOL attachments, Sage sync).
 */

import { useState, useEffect, useCallback } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import type { TimelineEvent, OrderTimelineResponse, OrderStatus } from '../../types/orders';
import { ORDER_STATUS_LABELS } from '../../types/orders';
import styles from './OrderTimelineTab.module.css';

// ── Props ──────────────────────────────────────────────────────────────

interface OrderTimelineTabProps {
  orderId: string;
}

// ── Icon mapping (emoji-based for zero dependencies) ───────────────────

const EVENT_ICONS: Record<string, string> = {
  mail: '📧',
  'plus-circle': '➕',
  edit: '✏️',
  send: '📤',
  'check-circle': '✅',
  'thumbs-up': '👍',
  package: '📦',
  'x-circle': '❌',
  'alert-triangle': '⚠️',
  database: '🗄️',
  'check-square': '☑️',
  'file-text': '📄',
  truck: '🚚',
  circle: '⚪',
};

// ── Color helpers ──────────────────────────────────────────────────────

const DOT_CLASS: Record<string, string> = {
  green: styles.dotGreen,
  blue: styles.dotBlue,
  amber: styles.dotAmber,
  red: styles.dotRed,
  gray: styles.dotGray,
};

const STATUS_BANNER_CLASS: Record<string, string> = {
  draft: styles.statusBannerGray,
  submission_failed: styles.statusBannerRed,
  submitted_to_sage: styles.statusBannerBlue,
  confirmed_in_sage: styles.statusBannerGreen,
  accepted: styles.statusBannerGreen,
  shipped: styles.statusBannerGreen,
  order_completed: styles.statusBannerGreen,
  cancelled: styles.statusBannerRed,
};

const STATUS_DOT_CLASS: Record<string, string> = {
  draft: styles.statusDotGray,
  submission_failed: styles.statusDotRed,
  submitted_to_sage: styles.statusDotBlue,
  confirmed_in_sage: styles.statusDotGreen,
  accepted: styles.statusDotGreen,
  shipped: styles.statusDotGreen,
  order_completed: styles.statusDotGreen,
  cancelled: styles.statusDotRed,
};

// ── Date formatting ────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (diffDays === 0) return `Today ${time}`;
  if (diffDays === 1) return `Yesterday ${time}`;
  if (diffDays < 7) {
    const day = d.toLocaleDateString('en-US', { weekday: 'short' });
    return `${day} ${time}`;
  }

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// ── Component ──────────────────────────────────────────────────────────

const OrderTimelineTab: React.FC<OrderTimelineTabProps> = ({ orderId }) => {
  const [timeline, setTimeline] = useState<OrderTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await portalOrderService.getOrderTimeline(orderId);
      setTimeline(data);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load timeline';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  // ── Loading / Error / Empty ─────────────────────────────────────────

  if (loading) {
    return <div className={styles.loadingState}>Loading timeline…</div>;
  }

  if (error) {
    return <div className={styles.errorState}>{error}</div>;
  }

  if (!timeline || timeline.events.length === 0) {
    return (
      <div className={styles.emptyState}>
        <span className={styles.emptyIcon}>📋</span>
        <span>No timeline events yet</span>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────

  const currentLabel =
    ORDER_STATUS_LABELS[timeline.current_status as OrderStatus] ??
    timeline.current_status;

  return (
    <div className={styles.container}>
      {/* Current status banner */}
      <div
        className={`${styles.statusBanner} ${
          STATUS_BANNER_CLASS[timeline.current_status] ?? styles.statusBannerGray
        }`}
      >
        <span
          className={`${styles.statusDot} ${
            STATUS_DOT_CLASS[timeline.current_status] ?? styles.statusDotGray
          }`}
        />
        Current status: <strong>{currentLabel}</strong>
        <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.7 }}>
          {timeline.event_count} event{timeline.event_count !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Timeline track */}
      <div className={styles.timeline}>
        {timeline.events.map((event, idx) => (
          <TimelineEventCard key={`${event.timestamp}-${idx}`} event={event} />
        ))}
      </div>
    </div>
  );
};

// ── Single event card ──────────────────────────────────────────────────

const TimelineEventCard: React.FC<{ event: TimelineEvent }> = ({ event }) => {
  const dotColor = DOT_CLASS[event.color] ?? DOT_CLASS.blue;
  const icon = EVENT_ICONS[event.icon] ?? EVENT_ICONS.circle;

  return (
    <div className={styles.event}>
      <span className={`${styles.dot} ${dotColor}`} />
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.iconWrapper}>
            <span className={styles.eventIcon}>{icon}</span>
            <span className={styles.title}>{event.title}</span>
          </div>
          <span className={styles.timestamp}>
            {formatTimestamp(event.timestamp)}
          </span>
        </div>
        {event.description && (
          <div className={styles.description}>{event.description}</div>
        )}
        {event.actor && (
          <span className={styles.actorBadge}>{event.actor}</span>
        )}
      </div>
    </div>
  );
};

export default OrderTimelineTab;
